import { useCallback, useEffect, useState } from 'react';
import { useNfcScanner } from '../../hooks/useNfcScanner';
import api from '../../services/api';
import type { NfcTagPairingResponse, PairNfcTagRequest } from '../../types/inspectionOrder';
import './NfcPairAction.css';

interface NfcPairActionProps {
  orderId: string;
  deviceId: string;
  currentNfcTagId?: string | null;
  onPairingSuccess: (nfcTagId: string) => void;
}

export const NfcPairAction = ({
  orderId,
  deviceId,
  currentNfcTagId,
  onPairingSuccess
}: NfcPairActionProps) => {
  const { isSupported, isScanning, error, lastRead, startScan, stopScan, clearError, clearLastRead } = useNfcScanner();
  const [isPairing, setIsPairing] = useState(false);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [conflictInfo, setConflictInfo] = useState<{
    deviceId: string;
    identifier: string;
    equipmentType: string;
    tagId: string;
  } | null>(null);

  const handlePairClick = useCallback(() => {
    if (isScanning) {
      stopScan();
    } else {
      setPairingError(null);
      setSuccessMessage(null);
      setConflictInfo(null);
      clearError();
      clearLastRead();
      void startScan();
    }
  }, [isScanning, stopScan, startScan, clearError, clearLastRead]);

  const pairTag = useCallback(async (tagId: string, force: boolean) => {
    setIsPairing(true);
    setPairingError(null);
    setSuccessMessage(null);

    try {
      const request: PairNfcTagRequest = { nfcTagId: tagId, force };
      const response = await api.put<NfcTagPairingResponse>(
        `technicians/me/inspection-orders/${orderId}/devices/${deviceId}/nfc-tag`,
        request
      );

      const result = response as unknown as NfcTagPairingResponse;

      if (result.conflict) {
        setConflictInfo({
          deviceId: result.conflictDeviceId || '',
          identifier: result.conflictDeviceIdentifier || 'Unbekannt',
          equipmentType: result.conflictDeviceEquipmentType || 'Unbekannt',
          tagId
        });
      } else if (result.success) {
        setSuccessMessage('NFC-Tag erfolgreich gekoppelt');
        onPairingSuccess(tagId);
        setConflictInfo(null);
      } else {
        setPairingError('Kopplung fehlgeschlagen');
      }
    } catch (pairErr) {
      console.error('NFC pairing error:', pairErr);
      setPairingError('NFC-Tag konnte nicht gekoppelt werden');
    } finally {
      setIsPairing(false);
    }
  }, [orderId, deviceId, onPairingSuccess]);

  const handleConfirmReassign = useCallback(() => {
    if (conflictInfo) {
      void pairTag(conflictInfo.tagId, true);
    }
  }, [conflictInfo, pairTag]);

  const handleCancelReassign = useCallback(() => {
    setConflictInfo(null);
    clearLastRead();
  }, [clearLastRead]);

  useEffect(() => {
    if (!lastRead || !lastRead.serialNumber || conflictInfo) {
      return;
    }

    void pairTag(lastRead.serialNumber, false);
    clearLastRead();
  }, [lastRead, conflictInfo, pairTag, clearLastRead]);

  if (!isSupported) {
    return null;
  }

  const displayError = pairingError || error;
  const currentNfcTagLabel = currentNfcTagId ? currentNfcTagId : 'Nicht gekoppelt';

  return (
    <div className="nfc-pair-action">
      <div className="nfc-pair-action__current">
        <span className="nfc-pair-action__current-label">Aktueller NFC-Tag:</span>
        <span className="nfc-pair-action__current-value">{currentNfcTagLabel}</span>
      </div>

      <button
        type="button"
        className={`nfc-pair-action__button ${isScanning ? 'nfc-pair-action__button--scanning' : ''}`}
        onClick={handlePairClick}
        disabled={isPairing || !!conflictInfo}
        aria-label={isScanning ? 'Scan abbrechen' : 'NFC-Tag koppeln'}
      >
        <span className="nfc-pair-action__icon" aria-hidden="true">
          {isScanning ? '📡' : '🏷️'}
        </span>
        <span className="nfc-pair-action__label">
          {isPairing ? 'Koppeln...' : isScanning ? 'Scannen...' : currentNfcTagId ? 'NFC-Tag ändern' : 'NFC-Tag koppeln'}
        </span>
      </button>

      {successMessage && (
        <div className="nfc-pair-action__success" role="status">
          {successMessage}
        </div>
      )}

      {displayError && (
        <div className="nfc-pair-action__error" role="alert">
          {displayError}
        </div>
      )}

      {conflictInfo && (
        <div className="nfc-pair-action__conflict" role="alertdialog" aria-modal="true">
          <div className="nfc-pair-action__conflict-content">
            <p className="nfc-pair-action__conflict-title">NFC-Tag bereits zugewiesen</p>
            <p className="nfc-pair-action__conflict-message">
              Dieser NFC-Tag ist bereits dem Gerät <strong>{conflictInfo.identifier}</strong> ({conflictInfo.equipmentType}) zugewiesen.
            </p>
            <p className="nfc-pair-action__conflict-question">
              Möchten Sie den Tag diesem Gerät zuweisen?
            </p>
            <div className="nfc-pair-action__conflict-actions">
              <button
                type="button"
                className="nfc-pair-action__conflict-cancel"
                onClick={handleCancelReassign}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="nfc-pair-action__conflict-confirm"
                onClick={handleConfirmReassign}
                disabled={isPairing}
              >
                {isPairing ? 'Wird zugewiesen...' : 'Tag zuweisen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
